"use client"

import { useState, useEffect } from "react"
import Link from "next/link"
import { useRouter, useSearchParams } from "next/navigation"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"
import axios from "axios"
import apiClient from "@/lib/axios-client"
import { toast } from "sonner"

import { Package, TrendingUp, Loader2, Info } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { AuthCardShell } from "@/components/auth/auth-card-shell"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"

import { API_ENDPOINTS } from "@/lib/api-endpoints";

type AccountType = "BUYER" | "SUPPLIER"

interface RegisterFormProps {
    dict: {
        back_to_site: string
        title: string
        subtitle: string
        step_1_label: string
        buyer_type: string
        buyer_desc: string
        supplier_type: string
        supplier_desc: string
        continue_btn: string
        have_account: string
        login_link: string

        company_label: string
        company_placeholder: string
        company_access_note: string
        firstname_label: string
        firstname_placeholder: string
        lastname_label: string
        lastname_placeholder: string
        email_label: string
        email_placeholder: string
        confirm_email_label: string
        confirm_email_placeholder: string
        phone_label: string
        phone_placeholder: string
        country_label: string
        country_placeholder: string
        countries: {
            // 1. Romania
            "ro": string

            // 2. Vecini direcți
            "md": string // Moldova
            "ua": string // Ucraina
            "bg": string // Bulgaria
            "rs": string // Serbia
            "hu": string // Ungaria

            // 3. Europa Centrală, Balcanii și de Est
            "sk": string // Slovacia
            "cz": string // Cehia
            "pl": string // Polonia
            "at": string // Austria
            "hr": string // Croația
            "si": string // Slovenia
            "ba": string // Bosnia și Herțegovina
            "me": string // Muntenegru
            "mk": string // Macedonia de Nord
            "al": string // Albania
            "gr": string // Grecia
            "by": string // Belarus
            "ru": string // Rusia

            // 4. Europa Central-Vestică și Italia (plus micro-statele)
            "de": string // Germania
            "it": string // Italia
            "sm": string // San Marino
            "va": string // Vatican
            "ch": string // Elveția
            "li": string // Liechtenstein
            "mc": string // Monaco

            // 5. Țările Baltice
            "lt": string // Lituania
            "lv": string // Letonia
            "ee": string // Estonia

            // 6. Europa de Vest
            "fr": string // Franța
            "be": string // Belgia
            "nl": string // Olanda
            "lu": string // Luxemburg

            // 7. Peninsula Iberică și Insulele Vestice
            "es": string // Spania
            "ad": string // Andorra
            "pt": string // Portugalia
            "gb": string // UK
            "ie": string // Irlanda

            // 8. Țările Nordice (inclusiv Islanda)
            "dk": string // Danemarca
            "se": string // Suedia
            "no": string // Norvegia
            "fi": string // Finlanda
            "is": string // Islanda

            // 9. Țările Insulare din Sud
            "cy": string // Cipru
            "mt": string // Malta
        }

        password_label: string
        password_placeholder: string
        confirm_password_label: string
        confirm_password_placeholder: string

        back_btn: string
        submit_btn: string
        toast_success_title: string
        toast_success_desc: string
        toast_error_title: string
    }
    lang: string
}

const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
const PHONE_PREFIXES = [
    // 1. Romania
    { code: "+40", country: "RO" },

    // 2. Vecini direcți
    { code: "+373", country: "MD" },
    { code: "+380", country: "UA" },
    { code: "+359", country: "BG" },
    { code: "+381", country: "RS" },
    { code: "+36", country: "HU" },

    // 3. Europa Centrală, Balcanii și de Est
    { code: "+421", country: "SK" },
    { code: "+420", country: "CZ" },
    { code: "+48", country: "PL" },
    { code: "+43", country: "AT" },
    { code: "+385", country: "HR" },
    { code: "+386", country: "SI" },
    { code: "+387", country: "BA" },
    { code: "+382", country: "ME" },
    { code: "+389", country: "MK" },
    { code: "+355", country: "AL" },
    { code: "+30", country: "GR" },
    { code: "+375", country: "BY" },
    { code: "+7", country: "RU" },

    // 4. Europa Central-Vestică și Italia
    { code: "+49", country: "DE" },
    { code: "+39", country: "IT" },
    { code: "+378", country: "SM" },
    { code: "+379", country: "VA" },
    { code: "+41", country: "CH" },
    { code: "+423", country: "LI" },
    { code: "+377", country: "MC" },

    // 5. Țările Baltice
    { code: "+370", country: "LT" },
    { code: "+371", country: "LV" },
    { code: "+372", country: "EE" },

    // 6. Europa de Vest
    { code: "+33", country: "FR" },
    { code: "+32", country: "BE" },
    { code: "+31", country: "NL" },
    { code: "+352", country: "LU" },

    // 7. Peninsula Iberică și Insulele Vestice
    { code: "+34", country: "ES" },
    { code: "+376", country: "AD" },
    { code: "+351", country: "PT" },
    { code: "+44", country: "GB" }, // Standardizat la "GB"
    { code: "+353", country: "IE" },

    // 8. Țările Nordice (inclusiv Islanda)
    { code: "+45", country: "DK" },
    { code: "+46", country: "SE" },
    { code: "+47", country: "NO" },
    { code: "+358", country: "FI" },
    { code: "+354", country: "IS" },

    // 9. Țările Insulare din Sud
    { code: "+357", country: "CY" },
    { code: "+356", country: "MT" }
].sort((a, b) => a.country.localeCompare(b.country));

import { PASSWORD_REGEX } from "@/lib/password-strength"
import { PasswordStrengthIndicator, DEFAULT_PASSWORD_LABELS } from "@/components/common/password-strength-indicator"

const REGISTER_PASSWORD_LABELS = DEFAULT_PASSWORD_LABELS

const registerSchema = z.object({
    role: z.enum(["BUYER", "SUPPLIER"]),
    companyName: z.string()
        .min(1, { message: "Company name is required" })
        .min(2, { message: "Company name is too short" }),

    firstName: z.string()
        .min(1, { message: "First name is required" })
        .min(2, { message: "Must be at least 2 characters" }),

    lastName: z.string()
        .min(1, { message: "Last name is required" })
        .min(2, { message: "Must be at least 2 characters" }),

    email: z.string()
        .min(1, { message: "Email is required" })
        .regex(emailRegex, { message: "Invalid email address" }),

    confirmEmail: z.string()
        .min(1, { message: "Please confirm your email" })
        .regex(emailRegex, { message: "Invalid email address" }),

    phonePrefix: z.string().min(1),

    phoneNumber: z.string()
        .min(1, { message: "Phone number is required" })
        .min(6, { message: "Phone number is too short" })
        .regex(/^\d+$/, { message: "Phone number must contain only digits" }),

    country: z.string()
        .min(1, { message: "Please select a country" }),

    password: z.string()
        .min(1, { message: "Password is required" })
        .min(10, { message: "Password must be at least 10 characters" })
        .regex(PASSWORD_REGEX, {
            message: "Must contain uppercase, lowercase, and a number (min 10 characters)"
        }),

    confirmPassword: z.string()
        .min(1, { message: "Please confirm your password" })

}).refine((data) => data.email === data.confirmEmail, {
    message: "Emails do not match",
    path: ["confirmEmail"],
}).refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
})

type RegisterFormValues = z.infer<typeof registerSchema>

export function RegisterForm({ dict, lang }: RegisterFormProps) {
    const router = useRouter()
    const searchParams = useSearchParams()

    const [registrationStep, setRegistrationStep] = useState(1)
    const [accountType, setAccountType] = useState<AccountType | null>(null)
    const [isLoading, setIsLoading] = useState(false)
    const inviteToken = searchParams.get("invite")?.trim() || ""
    const invitedEmail = searchParams.get("email")?.trim() || ""
    const invitedCompany = searchParams.get("company")?.trim() || ""
    const hasInvite = inviteToken.length > 0

    // 2. Initializare Formular
    const form = useForm<RegisterFormValues>({
        resolver: zodResolver(registerSchema),
        defaultValues: {
            role: "BUYER",
            companyName: "",
            firstName: "",
            lastName: "",
            email: "",
            confirmEmail: "",
            phonePrefix: "+40",
            phoneNumber: "",
            country: "",
            password: "",
            confirmPassword: ""
        },
        mode: "onChange"
    })

    useEffect(() => {
        if (!hasInvite) return

        if (invitedEmail) {
            form.setValue("email", invitedEmail, { shouldValidate: true })
            form.setValue("confirmEmail", invitedEmail, { shouldValidate: true })
        }

        if (invitedCompany) {
            form.setValue("companyName", invitedCompany, { shouldValidate: true })
        }
    }, [form, hasInvite, invitedCompany, invitedEmail])

    const handleRegistrationNext = () => {
        if (accountType) {
            form.setValue("role", accountType)
            setRegistrationStep(2)
        }
    }

    // 3. Functia de Submit
    async function onSubmit(data: RegisterFormValues) {
        setIsLoading(true)
        form.clearErrors(["companyName", "email", "confirmEmail"])

        try {
            let cleanNumber = data.phoneNumber.trim();
            cleanNumber = cleanNumber.replace(/^0+/, "");

            const payload = {
                companyName: data.companyName.trim(),
                firstName: data.firstName.trim(),
                lastName: data.lastName.trim(),
                email: data.email.trim().toLowerCase(),
                phonePrefix: data.phonePrefix,
                phoneNumber: cleanNumber,
                country: data.country,
                password: data.password,
                role: data.role,
                invitationToken: hasInvite ? inviteToken : undefined,
            }

            await apiClient.post(API_ENDPOINTS.REGISTER, payload)

            toast.success(dict.toast_success_title, {
                description: dict.toast_success_desc,
                duration: 8000,
            })

            router.push(`/${lang}/login`)

        } catch (error) {
            //console.error("Registration error:", error)
            // Mesaj default
            let errorMessage = "Something went wrong. Please try again."
            // Verificăm dacă este o eroare de la Axios (backend)
            if (axios.isAxiosError(error) && error.response) {
                // Cazul specific: 429 Too Many Requests (Rate limit)
                if (error.response.status === 429) {
                    errorMessage = error.response.data?.message || "Too many requests. Please try again later."
                }
                // Cazul specific: 409 Conflict (Email existent)
                else if (error.response.status === 409) {
                    // Încercăm să luăm mesajul din backend, altfel punem unul noi
                    const backendMsg = error.response.data?.message || "This email is already registered.";
                    // Varianta PRO: Punem eroarea direct sub câmpul de email
                    form.setError("email", {
                        type: "manual",
                        message: backendMsg
                    });
                    form.setFocus("email");
                    errorMessage = backendMsg; // Pentru toast
                }
                // Alte erori (400, 500, etc.)
                else {
                    const backendMsg = error.response.data?.message || `Server error: ${error.response.status}`
                    errorMessage = backendMsg

                    const normalizedMsg = String(backendMsg).toLowerCase()
                    const companyPolicyError =
                        normalizedMsg.includes("company") ||
                        normalizedMsg.includes("approved") ||
                        normalizedMsg.includes("registrations are disabled")

                    if (companyPolicyError) {
                        form.setError("companyName", {
                            type: "manual",
                            message: backendMsg,
                        })
                        form.setError("email", {
                            type: "manual",
                            message: backendMsg,
                        })
                        form.setFocus("companyName")
                    }
                }
            }
            // Afișăm notificarea Sonner
            toast.error(dict.toast_error_title, {
                description: errorMessage,
            })
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <AuthCardShell
            title={dict.title}
            description={undefined}
            backHref="/"
            backLabel={dict.back_to_site}
        >

            {/* --- STEP 1 --- */}
            {registrationStep === 1 && (
                <>
                    <div className="space-y-4">
                        <Label className="text-base">{dict.step_1_label}</Label>
                        <div className="grid grid-cols-2 gap-3">
                            <Button
                                variant={accountType === "BUYER" ? "default" : "outline"}
                                className={`h-auto py-8 flex-col gap-3 ${accountType === "BUYER" ? "ring-2 ring-primary ring-offset-2" : ""}`}
                                onClick={() => setAccountType("BUYER")}
                            >
                                <Package className="h-8 w-8" />
                                <div className="text-center">
                                    <div className="font-semibold text-base">{dict.buyer_type}</div>
                                    <div className="text-xs opacity-80 mt-1">{dict.buyer_desc}</div>
                                </div>
                            </Button>
                            <Button
                                variant={accountType === "SUPPLIER" ? "default" : "outline"}
                                className={`h-auto py-8 flex-col gap-3 ${accountType === "SUPPLIER" ? "ring-2 ring-primary ring-offset-2" : ""}`}
                                onClick={() => setAccountType("SUPPLIER")}
                            >
                                <TrendingUp className="h-8 w-8" />
                                <div className="text-center">
                                    <div className="font-semibold text-base">{dict.supplier_type}</div>
                                    <div className="text-xs opacity-80 mt-1">{dict.supplier_desc}</div>
                                </div>
                            </Button>
                        </div>
                    </div>
                    <Button className="w-full mt-6" size="lg" onClick={handleRegistrationNext} disabled={!accountType}>
                        {dict.continue_btn}
                    </Button>
                    <div className="mt-4 text-center text-sm text-muted-foreground">
                        {dict.have_account}{" "}
                        <Link href={`/${lang}/login`} className="text-primary hover:underline">
                            {dict.login_link}
                        </Link>
                    </div>
                </>
            )}

            {/* --- STEP 2 --- */}
            {registrationStep === 2 && (
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">

                        <FormField
                            control={form.control}
                            name="companyName"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="flex items-center gap-1.5">
                                        <span>{dict.company_label} *</span>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <button
                                                    type="button"
                                                    className="inline-flex text-muted-foreground hover:text-foreground transition-colors"
                                                    aria-label="Company access policy"
                                                >
                                                    <Info className="h-3.5 w-3.5" />
                                                </button>
                                            </TooltipTrigger>
                                            <TooltipContent side="right" className="max-w-xs leading-relaxed">
                                                {dict.company_access_note}
                                            </TooltipContent>
                                        </Tooltip>
                                    </FormLabel>
                                    <FormControl>
                                            <Input autoComplete="organization" placeholder={dict.company_placeholder} disabled={hasInvite} {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <div className="grid grid-cols-2 gap-3">
                            <FormField
                                control={form.control}
                                name="firstName"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>{dict.firstname_label} *</FormLabel>
                                        <FormControl>
                                            <Input autoComplete="given-name" placeholder={dict.firstname_placeholder} {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="lastName"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>{dict.lastname_label} *</FormLabel>
                                        <FormControl>
                                            <Input autoComplete="family-name" placeholder={dict.lastname_placeholder} {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>

                        <div className="space-y-3">
                            <FormField
                                control={form.control}
                                name="email"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>{dict.email_label} *</FormLabel>
                                        <FormControl>
                                            <Input type="email" autoComplete="email" placeholder={dict.email_placeholder} disabled={hasInvite} {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="confirmEmail"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>{dict.confirm_email_label} *</FormLabel>
                                        <FormControl>
                                            <Input type="email" autoComplete="email" placeholder={dict.confirm_email_placeholder} disabled={hasInvite} {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>

                        <div className="space-y-2">
                            <Label>{dict.phone_label} *</Label>
                            <div className="flex gap-2">
                                {/* Selector Prefix */}
                                <FormField
                                    control={form.control}
                                    name="phonePrefix"
                                    render={({ field }) => (
                                        <FormItem className="shrink-0 w-[100px]">
                                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                                                <FormControl>
                                                    <SelectTrigger>
                                                        <SelectValue placeholder="+40" />
                                                    </SelectTrigger>
                                                </FormControl>
                                                <SelectContent>
                                                    <ScrollArea className="h-[200px]">
                                                        {PHONE_PREFIXES.map((prefix) => (
                                                            <SelectItem key={prefix.country} value={prefix.code}>
                                                                <span className="flex justify-between w-full gap-2">
                                                                    <span className="text-muted-foreground font-mono">{prefix.country}</span>
                                                                    <span>{prefix.code}</span>
                                                                </span>
                                                            </SelectItem>
                                                        ))}
                                                    </ScrollArea>
                                                </SelectContent>
                                            </Select>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                {/* Input Telefon */}
                                <FormField
                                    control={form.control}
                                    name="phoneNumber"
                                    render={({ field }) => (
                                        <FormItem className="flex-1">
                                            <FormControl>
                                                <Input
                                                    type="tel"
                                                    autoComplete="tel-national"
                                                    placeholder={dict.phone_placeholder}
                                                    {...field}

                                                    onBlur={(e) => {
                                                        const val = e.target.value;
                                                        // Dacă începe cu 0, îl ștergem și actualizăm formularul vizual
                                                        if (val.startsWith("0")) {
                                                            const cleanVal = val.replace(/^0+/, "");
                                                            form.setValue("phoneNumber", cleanVal, {
                                                                shouldValidate: true // Re-validează câmpul după modificare
                                                            });
                                                        }
                                                        // Apelăm și onBlur-ul original al React Hook Form
                                                        field.onBlur();
                                                    }}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>
                        </div>

                        <FormField
                            control={form.control}
                            name="country"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>{dict.country_label} *</FormLabel>
                                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                                        <FormControl>
                                            <SelectTrigger>
                                                <SelectValue placeholder={dict.country_placeholder} />
                                            </SelectTrigger>
                                        </FormControl>

                                        <SelectContent>
                                            {/* Folosim ScrollArea pentru ca lista va fi lunga */}
                                            <ScrollArea className="h-50 w-full pr-4">
                                                {Object.entries(dict.countries)
                                                    .sort(([, nameA], [, nameB]) =>
                                                        // Ignoram primele caractere (steagul + spatiu) pentru sortare
                                                        nameA.substring(3).localeCompare(nameB.substring(3))
                                                    )
                                                    .map(([code, name]) => (
                                                        <SelectItem key={code} value={code}>
                                                            {name}
                                                        </SelectItem>
                                                    ))}
                                            </ScrollArea>
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <FormField
                                control={form.control}
                                name="password"
                                render={({ field }) => (
                                    <FormItem className="sm:col-span-2">
                                        <FormLabel>{dict.password_label} *</FormLabel>
                                        <FormControl>
                                            <Input type="password" autoComplete="new-password" placeholder={dict.password_placeholder} {...field} />
                                        </FormControl>
                                        <PasswordStrengthIndicator value={field.value || ""} labels={REGISTER_PASSWORD_LABELS} />
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="confirmPassword"
                                render={({ field }) => (
                                    <FormItem className="sm:col-span-2">
                                        <FormLabel>{dict.confirm_password_label} *</FormLabel>
                                        <FormControl>
                                            <Input type="password" autoComplete="new-password" placeholder={dict.confirm_password_placeholder} {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>

                        <div className="flex gap-3 pt-2">
                            <Button type="button" variant="outline" className="w-1/3" onClick={() => setRegistrationStep(1)}>
                                {dict.back_btn}
                            </Button>
                            <Button type="submit" className="w-2/3" disabled={isLoading}>
                                {isLoading ? (
                                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {dict.submit_btn}</>
                                ) : (
                                    dict.submit_btn
                                )}
                            </Button>
                        </div>
                    </form>
                </Form>
            )}
        </AuthCardShell>
    )
}